import type { PlaceResult } from "@/lib/api/misc";
import { normalizePlaceDisplay } from "./PlaceAutocomplete.resolve";

/**
 * Option-list shaping for PlaceAutocomplete (C07/C20, 2026-10-07).
 *
 * A wrong pick is a wrong chart, so the list favours certainty over breadth:
 * an exact name match stands alone (no junk row 2 for a stray tap to land on),
 * same-spot duplicates collapse, and different towns that share a label are
 * told apart by coordinates instead of one being hidden.
 */

const SAME_SPOT_DEGREES = 0.01;

function isExactPlace(normalizedQuery: string, place: PlaceResult): boolean {
  return (
    normalizePlaceDisplay(place.name) === normalizedQuery ||
    normalizePlaceDisplay(place.display) === normalizedQuery
  );
}

/**
 * When the typed text exactly names one or more places, show only those (in
 * their existing order: local first, then India-first API rows). Otherwise the
 * list is unchanged. "New Delhi" therefore never shows Patna beneath it.
 */
export function exactMatchesAlone(query: string, results: PlaceResult[]): PlaceResult[] {
  const normalizedQuery = normalizePlaceDisplay(query);
  if (!normalizedQuery) return results;
  const exact = results.filter((place) => isExactPlace(normalizedQuery, place));
  return exact.length > 0 ? exact : results;
}

function isSameSpot(a: PlaceResult, b: PlaceResult): boolean {
  return (
    Math.abs(a.latitude - b.latitude) < SAME_SPOT_DEGREES &&
    Math.abs(a.longitude - b.longitude) < SAME_SPOT_DEGREES
  );
}

/** Drop a row that repeats an earlier row's label at the same spot. */
export function dedupeSamePlaces(results: PlaceResult[]): PlaceResult[] {
  const kept: PlaceResult[] = [];
  for (const place of results) {
    const label = normalizePlaceDisplay(place.display);
    const duplicate = kept.some(
      (other) => normalizePlaceDisplay(other.display) === label && isSameSpot(other, place)
    );
    if (!duplicate) kept.push(place);
  }
  return kept;
}

/** Labels shared by two or more different towns in the visible list. */
export function sharedPlaceLabels(results: PlaceResult[]): Set<string> {
  const counts = new Map<string, number>();
  for (const place of results) {
    const label = normalizePlaceDisplay(place.display);
    counts.set(label, (counts.get(label) ?? 0) + 1);
  }
  return new Set([...counts].filter(([, count]) => count > 1).map(([label]) => label));
}

/** "16.82°N, 75.72°E" — the honest tie-breaker when the gazetteer has no district. */
export function placeCoordinateLabel(place: PlaceResult): string {
  const lat = `${Math.abs(place.latitude).toFixed(2)}°${place.latitude >= 0 ? "N" : "S"}`;
  const lon = `${Math.abs(place.longitude).toFixed(2)}°${place.longitude >= 0 ? "E" : "W"}`;
  return `${lat}, ${lon}`;
}
